import { Component, Input, OnInit } from '@angular/core';
import { AdminService } from 'src/app/services/admin.service';
import { StringLocalizerService } from "src/app/services/string-localizer.service";
import { CommonModule  } from '@angular/common';
import { Role } from 'src/app/models/admin/role';
import { IntegrationDetails } from 'src/app/models/integration-details';
import { IntegrationPermission } from 'src/app/models/admin/permissions/integration-permission';

@Component({
    selector: 'app-permissions-integration',
    imports: [CommonModule],
    templateUrl: './permissions-integration.component.html',
    styleUrls: ['./permissions-integration.component.css'] // fixed: styleUrl -> styleUrls
})
export class PermissionsIntegrationComponent implements OnInit {
  public strings: Record<string, string> = {};
  public selectedRole: Role | undefined;
  integrationPermissions: IntegrationPermission | null = null;

  @Input() index = 0;
  @Input() integration!: IntegrationDetails;          // definite assignment
  @Input() fullAccessPermission = false;

  constructor(
    private readonly adminService: AdminService,
    private readonly stringLocalizerService: StringLocalizerService
  ) {
    this.stringLocalizerService.strings.subscribe(s => {
      if (s) {
        this.strings = this.stringLocalizerService.getSection('admin');
      }
    });

    this.adminService.$selectedRole.subscribe(r => {
      if (r) {
        this.selectedRole = r;
        this.resolveIntegrationPermissions();          // recompute when role arrives/changes
      }
    });
  }

  ngOnInit(): void {
    this.resolveIntegrationPermissions();              // will no-op if data not ready yet
  }

  ngOnChanges(): void {
    this.resolveIntegrationPermissions();              // recompute when @Input integration changes
  }

  private resolveIntegrationPermissions(): void {
    if (!this.selectedRole || !this.integration) {
      return;
    }
    this.integrationPermissions =
      this.selectedRole.permissions.integrationPermissions.integrations
        .find(i => i.name === this.integration.name) ?? null;
  }

  public integrationPermissionEnabled(permissionName: string): boolean {
    if (!this.integrationPermissions) {
      return this.selectedRole?.permissions.integrationPermissions.admin ?? false;
    }
    if (this.integrationPermissions.permission.split(',').some(p => p.trim() === permissionName)) {
      return true;
    }
    return this.selectedRole?.permissions.integrationPermissions.admin ?? false;
  }

  public toggleIntegrationPermission(permissionName: string) {
    // Check if SelectedRole is defined
    if (!this.selectedRole) return;

    // If integrationPermissions is null, it means no specific permissions set yet for this integration
    if (!this.integrationPermissions) {
      this.selectedRole.permissions.integrationPermissions.integrations
        .push({ name: this.integration.name, permission: permissionName });
      this.resolveIntegrationPermissions();
    } else {
      let permissionsArray = this.integrationPermissions.permission
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0 && p !== 'None');

      if (permissionsArray.includes(permissionName)) {
        permissionsArray = permissionsArray.filter(p => p !== permissionName);
      } else {
        permissionsArray.push(permissionName);
      } 

      this.integrationPermissions.permission =
        permissionsArray.length ? permissionsArray.join(', ') : 'None';
    }
    this.adminService.addRoleToUpdatedCollection(this.selectedRole);
  }

  public integrationsFullAccessEnabled(): boolean {
    return this.selectedRole?.permissions.integrationPermissions.admin ?? false;
  }

  public integrationAdminEnabled(): boolean {
    if (this.integrationsFullAccessEnabled()) return true;
    return !!this.integrationPermissions?.permission
      .split(',')
      .map(p => p.trim())
      .some(p => p === 'Admin');
  }

  public toggleIntegrationAdmin() {
    if (!this.selectedRole) return;
    const permsRoot = this.selectedRole.permissions.integrationPermissions;
    permsRoot.admin = !permsRoot.admin;
    permsRoot.integrations.forEach(integration => {
      const list = integration.permission.split(',').map(p => p.trim()).filter(p => p);
      const hasAdmin = list.includes('Admin');
      if (permsRoot.admin && !hasAdmin) {
        list.push('Admin');
      } else if (!permsRoot.admin && hasAdmin) {
        integration.permission = list.filter(p => p !== 'Admin').join(', ') || 'None';
        return;
      }
      integration.permission = list.join(', ') || 'None';
    });
    this.adminService.addRoleToUpdatedCollection(this.selectedRole);
  }
}
